import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  useTheme
} from '@mui/material';
import {
  Error as ErrorIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ErrorPageProps {
  errorMessage?: string;
  showDetails?: boolean;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ 
  errorMessage = "Ops! Algo deu errado", 
  showDetails = false 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: 3
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid component={'div' as React.ElementType} item xs={12}>
              <ErrorIcon
                sx={{
                  fontSize: { xs: 80, md: 120 },
                  color: 'error.main',
                  mb: 2
                }}
              />
              
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: 'text.primary',
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                {errorMessage}
              </Typography>
              
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                Encontramos um problema inesperado. Nossa equipe foi notificada e está trabalhando para resolver isso.
              </Typography>
              
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                Você pode tentar recarregar a página ou voltar à página inicial.
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<HomeIcon />}
                  onClick={handleGoHome}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  Página Inicial
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  Recarregar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Se o problema persistir, entre em contato conosco em{' '}
            <a 
              href="mailto:contato@wgtech.dev"
              style={{ 
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              contato@wgtech.dev
            </a>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ErrorPage;